// Modified by AI on 06/10/2026. Edit #1.
// Modified by AI on 06/12/2026. Edit #9.
import React, {
    useState, useCallback, useRef,
    useMemo, useEffect,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isUndefined } from 'lodash';

import settings from '../../config/settings';
import BackendConnector from '../api/backendConnector';
import Logger from '../services/logger';
import SearchCriteriaService from '../services/SearchCriteriaService';
import CriteriaConfigHttpService from '../api/CriteriaConfigHttpService';
import { QUERY_KEYS, THIRTY_MINUTES_MS } from '../constants/queryKeys';

const SettingsContext = React.createContext(settings);

export const SettingsProvider = SettingsContext.Provider;

// Modified by AI on 06/10/2026. Edit #2.
export const AppWithSettings = ({ children }) => {
    const queryClient = useQueryClient();
    const [isAuthComplete, setIsAuthComplete] = useState(false);

    // Refs to track lazy loader state for promise caching (preserve existing behavior)
    const positionCriteriaConfigPromise = useRef();
    const fastRatingCriteriaConfigPromise = useRef();
    const gdprLocationsFetchPromise = useRef();
    const translatedCountriesListFetchPromise = useRef();

    // Modified by AI on 06/12/2026. Edit #10.
    // Modified by AI on 07/01/2026. Edit #13.
    // Eager fetch: system configuration
    const {
        data: systemConfigurationRaw,
        isLoading: isLoadingConfig,
        error: configError,
    } = useQuery({
        queryKey: QUERY_KEYS.settings.config,
        queryFn: () => BackendConnector.get('/system/crmDefaults'),
        staleTime: THIRTY_MINUTES_MS,
        gcTime: THIRTY_MINUTES_MS,
    });

    const systemConfiguration = systemConfigurationRaw ?? {};

    // Modified by AI on 06/12/2026. Edit #11.
    // Modified by AI on 07/01/2026. Edit #14.
    // Eager fetch: system preferences
    const {
        data: systemPreferencesRaw,
        isLoading: isLoadingPreferences,
        error: preferencesError,
    } = useQuery({
        queryKey: QUERY_KEYS.settings.preferences,
        queryFn: () => BackendConnector.get('/system/preferences'),
        staleTime: THIRTY_MINUTES_MS,
        gcTime: THIRTY_MINUTES_MS,
    });

    const systemPreferences = systemPreferencesRaw ?? {};

    // Modified by AI on 06/12/2026. Edit #12.
    // Error handling: log errors but don't block rendering
    useEffect(() => {
        if (configError) {
            Logger.error(configError);
        }
    }, [configError]);

    useEffect(() => {
        if (preferencesError) {
            Logger.error(preferencesError);
        }
    }, [preferencesError]);

    // Render-blocking logic: wait for both config and preferences
    const fetched = !isLoadingConfig && !isLoadingPreferences;

    // Modified by AI on 06/10/2026. Edit #3.
    // Lazy loader: position criteria config with localStorage fallback
    const fetchPositionCriteriaConfig = useCallback(async () => {
        try {
            // Check if already cached in query cache
            const cached = queryClient.getQueryData(QUERY_KEYS.settings.positionCriteria);
            if (!isUndefined(cached)) {
                return cached;
            }

            // Use promise ref to prevent duplicate calls (preserve existing behavior)
            if (!positionCriteriaConfigPromise.current) {
                positionCriteriaConfigPromise.current = (async () => {
                    const result = await queryClient.fetchQuery({
                        queryKey: QUERY_KEYS.settings.positionCriteria,
                        queryFn: async () => {
                            let fetchedConfig = await CriteriaConfigHttpService.getPositionCriteriaConfig();

                            // localStorage fallback if API returns falsy
                            if (!fetchedConfig) {
                                fetchedConfig = JSON.parse(window.localStorage.getItem('positionCriteriaConfig') || '{}') || {};
                            } else {
                                // Save to localStorage on success
                                window.localStorage.setItem('positionCriteriaConfig', JSON.stringify(fetchedConfig));
                            }

                            return fetchedConfig;
                        },
                        staleTime: THIRTY_MINUTES_MS,
                        gcTime: THIRTY_MINUTES_MS,
                    });
                    return result;
                })();
            }

            return await positionCriteriaConfigPromise.current;
        } catch (e) {
            Logger.error(e);
            return {};
        }
    }, [queryClient]);

    // Modified by AI on 06/10/2026. Edit #4.
    // Lazy loader: fast rating criteria config
    const fetchFastRatingCriteriaConfig = useCallback(async () => {
        try {
            const cached = queryClient.getQueryData(QUERY_KEYS.settings.fastRatingCriteria);
            if (!isUndefined(cached)) {
                return cached;
            }

            if (!fastRatingCriteriaConfigPromise.current) {
                fastRatingCriteriaConfigPromise.current = queryClient.fetchQuery({
                    queryKey: QUERY_KEYS.settings.fastRatingCriteria,
                    queryFn: () => CriteriaConfigHttpService.getFastRatingCriteriaConfig(),
                    staleTime: THIRTY_MINUTES_MS,
                    gcTime: THIRTY_MINUTES_MS,
                });
            }

            return await fastRatingCriteriaConfigPromise.current;
        } catch (e) {
            Logger.error(e);
            return {};
        }
    }, [queryClient]);

    // Modified by AI on 06/10/2026. Edit #5.
    // Lazy loader: GDPR locations
    const fetchGdprLocations = useCallback(async () => {
        try {
            const cached = queryClient.getQueryData(QUERY_KEYS.settings.gdprLocations);
            if (!isUndefined(cached)) {
                return cached;
            }

            if (!gdprLocationsFetchPromise.current) {
                gdprLocationsFetchPromise.current = queryClient.fetchQuery({
                    queryKey: QUERY_KEYS.settings.gdprLocations,
                    queryFn: () => SearchCriteriaService.getGdprLocations(),
                    staleTime: THIRTY_MINUTES_MS,
                    gcTime: THIRTY_MINUTES_MS,
                });
            }

            return await gdprLocationsFetchPromise.current;
        } catch (e) {
            Logger.error(e);
            return null;
        }
    }, [queryClient]);

    // Modified by AI on 06/10/2026. Edit #6.
    // Lazy loader: translated countries list
    const fetchTranslatedCountriesList = useCallback(async () => {
        try {
            const cached = queryClient.getQueryData(QUERY_KEYS.settings.translatedCountries);
            if (!isUndefined(cached)) {
                return cached;
            }

            if (!translatedCountriesListFetchPromise.current) {
                translatedCountriesListFetchPromise.current = queryClient.fetchQuery({
                    queryKey: QUERY_KEYS.settings.translatedCountries,
                    queryFn: () => SearchCriteriaService.getTranslatedCountriesList(),
                    staleTime: THIRTY_MINUTES_MS,
                    gcTime: THIRTY_MINUTES_MS,
                });
            }

            return await translatedCountriesListFetchPromise.current;
        } catch (e) {
            Logger.error(e);
            return null;
        }
    }, [queryClient]);

    // Modified by AI on 06/10/2026. Edit #7.
    // Method for AuthorizationContext to trigger refetch
    const fetchSystemConfigAndPreferences = useCallback(async () => {
        try {
            await Promise.all([
                queryClient.refetchQueries({ queryKey: QUERY_KEYS.settings.config }),
                queryClient.refetchQueries({ queryKey: QUERY_KEYS.settings.preferences }),
            ]);
        } catch (error) {
            Logger.error(error);
        }
    }, [queryClient]);

    // Modified by AI on 06/10/2026. Edit #8.
    const contextValue = useMemo(() => ({
        environmentSettings: settings,
        systemConfiguration,
        systemPreferences,
        getGdprLocations: fetchGdprLocations,
        getTranslatedCountriesList: fetchTranslatedCountriesList,
        getPositionCriteriaConfig: fetchPositionCriteriaConfig,
        getFastRatingCriteriaConfig: fetchFastRatingCriteriaConfig,
        fetchSystemConfigAndPreferences,
        onAuthCompleteCallback: () => setIsAuthComplete(true),
        isAllSettingsFetched: isAuthComplete && fetched,
    }), [
        systemConfiguration,
        systemPreferences,
        fetchGdprLocations,
        fetchTranslatedCountriesList,
        fetchPositionCriteriaConfig,
        fetchFastRatingCriteriaConfig,
        fetchSystemConfigAndPreferences,
        isAuthComplete,
        fetched,
    ]);

    return (
        <SettingsProvider value={contextValue}>
            { fetched && children }
        </SettingsProvider>
    );
};

export default SettingsContext;
